/**
 * The cheap halves of the backend's own validators, for feedback while typing. The backend
 * decides: it also turns down common and breached passwords and ones too like the user.
 */
export const MIN_PASSWORD_LENGTH = 12;

/** A run of three or more characters typed twice in a row, e.g. `ABCD1234ABCD1234`. */
const REPEATED_PATTERN = /(.{3,})\1/;
const COMMON_PATTERN = /(abcdef|qwerty|012345|password|letmein)/i;

export interface PasswordRule {
  id: 'length' | 'not-numeric' | 'not-personal';
  passed: boolean;
}

function isCommonOrRepeated(password: string): boolean {
  return (
    new Set(password).size <= 4 ||
    /(.)\1{2,}/.test(password) ||
    REPEATED_PATTERN.test(password) ||
    COMMON_PATTERN.test(password)
  );
}

export function passwordRules(
  password: string,
  email = '',
  firstName = '',
  lastName = ''
): PasswordRule[] {
  const lower = password.toLowerCase();
  // Parts under three letters match too much by chance to say anything.
  const personal = [email.split('@')[0] ?? '', firstName, lastName]
    .map((part) => part.toLowerCase())
    .filter((part) => part.length >= 3);
  return [
    { id: 'length', passed: password.length >= MIN_PASSWORD_LENGTH },
    { id: 'not-numeric', passed: password.length > 0 && !/^\d+$/.test(password) },
    {
      id: 'not-personal',
      passed: password.length > 0 && !personal.some((part) => lower.includes(part)),
    },
  ];
}

export type PasswordStrength = 0 | 1 | 2 | 3 | 4;

/** A strength estimate based on length, character variety and common patterns; a hint only. */
export function passwordStrength(password: string): PasswordStrength {
  if (!password) return 0;

  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= MIN_PASSWORD_LENGTH) score += 1;
  if (password.length >= 16) score += 1;

  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((pattern) =>
    pattern.test(password)
  ).length;
  if (classes >= 3) score += 1;
  if (isCommonOrRepeated(password)) score -= 2;

  return Math.max(0, Math.min(4, score)) as PasswordStrength;
}
